import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { searchRegistry } from '../api/skillScoutApi';
import { InstallCmd } from '../components/InstallCmd';
import { TypeBadge } from '../components/TypeBadge';
import type { RegistryToolResponse } from '../types/api';

export function ToolDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [tool, setTool] = useState<RegistryToolResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setTool(null);
    setError(null);
    setLoading(true);
    searchRegistry(slug, [], 50)
      .then((results) => {
        const found = results.find((t) => t.slug === slug) ?? null;
        setTool(found);
      })
      .catch((e: unknown) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-white flex items-center justify-center">
        <p className="text-gray-400">Loading…</p>
      </div>
    );
  }

  if (error || !tool) {
    return (
      <div className="min-h-screen bg-background text-white">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Link to="/registry" className="text-secondary text-sm hover:underline">
            ← Back to Registry
          </Link>
          <div className="mt-6 rounded-xl bg-red-900/40 border border-red-700 p-4 text-sm text-red-300">
            {error ?? 'Tool not found.'}
          </div>
        </div>
      </div>
    );
  }

  const formattedDate = tool.last_published
    ? new Date(tool.last_published).toLocaleDateString()
    : null;

  return (
    <div className="min-h-screen bg-background text-white">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <Link to="/registry" className="text-secondary text-sm hover:underline">
          ← Back to Registry
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold">{tool.name}</h1>
          <TypeBadge type={tool.type} />
          {tool.verified && (
            <span className="text-xs text-secondary font-medium">✓ Verified</span>
          )}
        </div>

        {/* API key warning */}
        {tool.requires_api_key && (
          <div className="rounded-xl bg-yellow-900/40 border border-yellow-700 p-4 text-sm text-yellow-300">
            ⚠ This tool requires an API key to use.
          </div>
        )}

        {/* Description */}
        {tool.description && (
          <p className="text-gray-300">{tool.description}</p>
        )}

        {/* Install command */}
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Install
          </h2>
          <InstallCmd cmd={tool.install_cmd} />
        </div>

        {/* Keywords */}
        {tool.keywords.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Keywords
            </h2>
            <div className="flex flex-wrap gap-2">
              {tool.keywords.map((kw) => (
                <span
                  key={kw}
                  className="rounded-full bg-surface px-3 py-1 text-xs text-gray-300"
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Meta */}
        <div className="rounded-xl bg-surface p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Weekly downloads</span>
            <span className="text-white font-medium">
              {tool.weekly_downloads.toLocaleString()}
            </span>
          </div>
          {formattedDate && (
            <div className="flex justify-between">
              <span className="text-gray-400">Last published</span>
              <span className="text-white">{formattedDate}</span>
            </div>
          )}
          {tool.source && (
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Source</span>
              <a
                href={tool.source}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary hover:underline text-xs truncate max-w-[60%]"
              >
                {tool.source}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
